import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getBaseUrl } from "../config";
import { getAuthApiHeaders } from "../../utils/common";

/**
 * Utility: base API URL
 * If you use REACT_APP_API_BASE_URL, the slice will prefix endpoints with it.
 * Otherwise it will call relative endpoints.
 */
const API_BASE = getBaseUrl();

/**
 * Thunk: extractCallDetails
 * POST /extract-call-details
 * Body shape:
 * {
 *   user_details: { Rep_Email: "..." },
 *   call_text: "..."
 * }
 *
 * Returns whatever the API returns (put in extractResult).
 */
export const extractCallDetails = createAsyncThunk(
  "callLog/extractCallDetails",
  async ({ repEmail, call_text }, { rejectWithValue }) => {
    try {
      const url = `${API_BASE}/extract-call-details`;
      const headers = {
        "Content-Type": "application/json",
        ...getAuthApiHeaders(),
      };

      const body = {
        user_details: {
          Rep_Email: repEmail || "",
        },
        call_text: call_text || "",
      };

      const resp = await axios.post(url, body, { headers, withCredentials: true });
      return resp.data;
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "extract-call-details failed";
      return rejectWithValue(msg);
    }
  },
);

/**
 * Thunk: submitCallLog
 * POST /call-logs
 * Expects the assembled payload from the parent (CallLogDialog).
 *
 * Example payload shape:
 * {
 *   salesRepName: "...",
 *   territoryName: "...",
 *   callDateTime: "...",
 *   callDuration: 30,
 *   callType: "normal" | "group",
 *   accountName: "DoctorID-123",
 *   institutionId: "HCO-123",
 *   attendees: ["HCP-1","HCP-2"],
 *   selectedProducts: [],
 *   selectedKeyMessages: [],
 *   address: "...",
 *   ...anyOtherMeta
 * }
 */
export const submitCallLog = createAsyncThunk(
  "callLog/submitCallLog",
  async (payload, { rejectWithValue }) => {
    try {
      const url = `${API_BASE}/databricks/writeback`;
      const headers = {
        "Content-Type": "application/json",
        ...getAuthApiHeaders(),
      };

      const resp = await axios.post(url, payload, { headers, timeout: 300000, withCredentials: true });
      return resp.data;
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "submit-call-log failed";
      return rejectWithValue(msg);
    }
  },
);

const initialState = {
  // draft holds the in-progress call log (optional)
  draft: null,

  // submit state
  submitting: false,
  submitError: null,
  lastSaved: null, // server response from last submit
  cacheByRepEmail: {},

  // extraction state
  extracting: false,
  extractError: null,
  extractResult: null,

  // generic error
  error: null,
};

const callLogSlice = createSlice({
  name: "callLog",
  initialState,
  reducers: {
    setDraft(state, action) {
      state.draft = action.payload;
      state.error = null;
    },
    updateDraftField(state, action) {
      const { field, value } = action.payload;
      state.draft = { ...(state.draft || {}), [field]: value };
    },
    clearDraft(state) {
      state.draft = null;
      state.error = null;
    },
    clearError(state) {
      state.error = null;
      state.submitError = null;
      state.extractError = null;
    },
  },
  extraReducers: (builder) => {
    // extractCallDetails lifecycle
    builder
      .addCase(extractCallDetails.pending, (state) => {
        state.extracting = true;
        state.extractError = null;
        state.extractResult = null;
      })
      .addCase(extractCallDetails.fulfilled, (state, action) => {
        state.extracting = false;
        state.extractResult = action.payload;
        state.extractError = null;

        const repEmail = action.meta?.arg?.repEmail;
        if (repEmail) {
          state.cacheByRepEmail[repEmail] = {
            data: action.payload,
            cachedAt: Date.now(),
          };
        }
      })

      .addCase(extractCallDetails.rejected, (state, action) => {
        state.extracting = false;
        state.extractError =
          action.payload || action.error?.message || "Extraction failed";
        state.extractResult = null;
      });

    // submitCallLog lifecycle
    builder
      .addCase(submitCallLog.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
      })
      .addCase(submitCallLog.fulfilled, (state, action) => {
        state.submitting = false;
        state.lastSaved = action.payload;
        state.submitError = null;
        state.draft = null;
      })
      .addCase(submitCallLog.rejected, (state, action) => {
        state.submitting = false;
        state.submitError =
          action.payload || action.error?.message || "Submit failed";
      });
  },
});

export const { setDraft, updateDraftField, clearDraft, clearError } =
  callLogSlice.actions;

export default callLogSlice.reducer;
